import * as ce from "../utils/custom_errors";
import {
  IMessageData,
  getLlmCase,
  isUserRole,
  LLMStreamEventRole,
  IOMainLLMMessage,
  LLMStreamEventCategory,
  startLLMStreamV2,
  v2CreateChatWithFiles,
  V2CreateResponse,
  v2CaseCreatedResponse,
  LLMStreamV2Params,
  documentFile,
  V2CreateRawResponse,
  v2CreateChat,
  Workflow,
  WorkflowPrompt,
  getWorkflows,
} from "./rest_utils";
import { getContentFile } from "./word_utils";
import {
  showLoadingIndicator,
  hideLoadingIndicator,
  IOMain,
  scrollToTargetSmooth,
  toastMessage,
} from "./common";
import { TaskCategory } from "../utils/task_utils";
import { translate } from "../locales/i18n";
import { runOfficeTask } from "./queue_utils";

export async function loadCaseMessages(
  caseId: string,
  mainIOElement: IOMain,
  category: LLMStreamEventCategory,
  taskCategory: TaskCategory
): Promise<void> {
  const intervalId = showLoadingIndicator(
    mainIOElement.mainContainer,
    "Lade gespeicherte Nachrichten..."
  );

  try {
    const caseData = await getLlmCase(caseId);
    const savedCaseMessages = caseData.messages;

    hideLoadingIndicator(mainIOElement.mainContainer, intervalId);

    let lastMessagePromptIndex = [-1];
    savedCaseMessages.sort((a, b) => a.timestamp - b.timestamp);

    // Group messages by workflow_run_id
    const workflowMessages = new Map<string, typeof savedCaseMessages>();
    const nonWorkflowMessages: typeof savedCaseMessages = [];

    for (const message of savedCaseMessages) {
      if (message.workflow_run_id) {
        if (!workflowMessages.has(message.workflow_run_id)) {
          workflowMessages.set(message.workflow_run_id, []);
        }
        workflowMessages.get(message.workflow_run_id)!.push(message);
      } else {
        nonWorkflowMessages.push(message);
      }
    }

    // Process workflow messages first
    if (caseData.llm_workflow_status) {
      for (const workflowRun of caseData.llm_workflow_status) {
        const workflowRunId = workflowRun.workflow_run_id;
        const messagesForWorkflow = workflowMessages.get(workflowRunId) || [];
        console.log("FOR WORKFLOW RUN", workflowRun);
        console.log("NO OF MESSAGES", messagesForWorkflow.length);
        // Find workflow name from your workflow list
        const workflow = (await (await getWorkflows()).getWorkflows()).find(
          (w) => w.id === workflowRun.workflow_id
        );
        if (!workflow) {
          continue;
        }
        const workflowName = workflow?.title || `Workflow ${workflowRun.workflow_id}`;
        const isTrafficWorkflow =
          workflow.parameterTypes.includes("word") && workflow.parameterTypes.includes("check");

        const startTime = new Date(workflowRun.start_time);

        for (const message of messagesForWorkflow) {
          const messageIOMAIN: IOMainLLMMessage = {
            ...message,
            type: message.role,
            role: (isUserRole(message.role) ? "user" : "assistant") as LLMStreamEventRole,
            category: category,
            isHistoryLoading: true,
          } as IOMainLLMMessage;

          const currentPromptIndex: number[] = getPromptIndexParts(message.prompt_id);

          if (taskCategory === "edit-document") {
            if (isUserRole(message.role)) {
              if (!isAGreaterB(currentPromptIndex, lastMessagePromptIndex)) {
                mainIOElement.resetMessageCounter();
              }
            }
          }

          lastMessagePromptIndex = currentPromptIndex;

          await mainIOElement.addLLMIO(
            messageIOMAIN,
            isTrafficWorkflow ? "traffic-light" : "workflow",
            workflowRunId,
            workflowName,
            startTime
          );
        }
        mainIOElement.messageRequestDone();
      }
    }

    // Process non-workflow messages
    for (const message of nonWorkflowMessages) {
      const messageIOMAIN: IOMainLLMMessage = {
        ...message,
        type: message.role,
        role: (isUserRole(message.role) ? "user" : "assistant") as LLMStreamEventRole,
        category: category,
        isHistoryLoading: true,
      } as IOMainLLMMessage;

      await mainIOElement.addLLMIO(messageIOMAIN);
    }

    mainIOElement.applyTaskFilter(taskCategory);
    setTimeout(() => {
      mainIOElement.scrollDown();
    }, 250);
  } catch (error) {
    console.error(error);
    toastMessage(translate("error-loading-message-history"), "error");
  } finally {
    hideLoadingIndicator(mainIOElement.mainContainer, intervalId);
    mainIOElement.messageRequestDone();
  }
}

function getPromptIndexParts(promptId: string): number[] {
  const match = promptId.match(/prompt_([\d.]+)/);
  if (!match) return [];

  return match[1].split(".").map((part) => parseInt(part, 10));
}

export function isAGreaterB(a: number[], b: number[]): boolean {
  const len = Math.max(a.length, b.length);

  for (let i = 0; i < len; i++) {
    const valA = a[i] ?? 0;
    const valB = b[i] ?? 0;

    if (valA > valB) return true; // only true if a is strictly greater than b
    if (valA < valB) return false;
  }

  return false; // equal or smaller
}

export async function createCaseWithFiles(
  documentFiles: documentFile[] = []
): Promise<V2CreateResponse> {
  try {
    if (documentFiles.length === 0) {
      documentFiles = [await getContentFile()];
    }
    const createResult: V2CreateResponse = {
      ...(await v2CreateChatWithFiles(documentFiles)),
      // isPDF: documentFile.originFileObj.type === "application/pdf",
    };
    return createResult;
  } catch (error) {
    console.error(error);
    throw new ce.CaseCreationError(translate("error-case-creation"));
  }
}

export async function createRawCase(): Promise<V2CreateRawResponse> {
  try {
    return await v2CreateChat();
  } catch (error) {
    console.error(error);
    throw new ce.CaseCreationError(translate("error-case-creation"));
  }
}

export async function makeLlmRequestV2(
  mainIOElement: IOMain,
  checkInputCallback: (isBusy: boolean) => void,
  llmStreamV2Params: LLMStreamV2Params,
  saveCaseId?: (caseId: string) => Promise<void>,
  messageDoneCallback?: () => void
): Promise<void> {
  try {
    checkInputCallback(true);

    mainIOElement.indicatorStart = showLoadingIndicator(
      mainIOElement.mainContainer,
      "Anfrage wird gesendet. Bitte warten..."
    );

    let caseId: string | null = null;

    mainIOElement.resetMessageCounter();

    llmStreamV2Params = {
      ...llmStreamV2Params,
      async onQuestion(data: IOMainLLMMessage) {
        mainIOElement.addLLMIO(data);
        // runOfficeTask(() => mainIOElement.addLLMIO(data));
      },
      async onAnswer(data: IOMainLLMMessage) {
        mainIOElement.addLLMIO(data);
        // runOfficeTask(() => mainIOElement.addLLMIO(data));
      },
      async onDocumentQuestion(data: IOMainLLMMessage) {
        mainIOElement.addLLMIO(data);
        // runOfficeTask(() => mainIOElement.addLLMIO(data));
      },
      async onDocumentAnswer(data: IOMainLLMMessage) {
        mainIOElement.addLLMIO(data);
        // runOfficeTask(() => mainIOElement.addLLMIO(data));
      },
      async onFollowupQuestion(data: IOMainLLMMessage) {
        mainIOElement.addLLMIO(data);
        // runOfficeTask(() => mainIOElement.addLLMIO(data));
      },
      async onFollowupAnswer(data: IOMainLLMMessage) {
        mainIOElement.addLLMIO(data);
        // runOfficeTask(() => mainIOElement.addLLMIO(data));
      },
      async onPlaybookQuestion(data: IOMainLLMMessage) {
        mainIOElement.addLLMIO(data);
        // runOfficeTask(() => mainIOElement.addLLMIO(data));
      },
      async onPlaybookAnswer(data: IOMainLLMMessage) {
        mainIOElement.addLLMIO(data);
        // runOfficeTask(() => mainIOElement.addLLMIO(data));
      },
      async onSummaryQuestion(data: IOMainLLMMessage) {
        mainIOElement.addLLMIO(data);
        // runOfficeTask(() => mainIOElement.addLLMIO(data));
      },
      async onSummaryAnswer(data: IOMainLLMMessage) {
        mainIOElement.addLLMIO(data);
        // runOfficeTask(() => mainIOElement.addLLMIO(data));
      },
      onCaseCreated(data: v2CaseCreatedResponse) {
        caseId = data.content;
      },
      onStatus(value: string) {
        // console.log("onStatus", value);
      },
      onError: () => {
        // console.log("onError"); - log specific error, if there is one
        toastMessage(translate("error-request-response"), "error");
      },
      onStart: () => {
        // console.log("onStart");
      },
      onClose: () => {
        console.log("on close of makeLLMRequestV2 running");
        if (mainIOElement.taskCategory === "playbook") {
          mainIOElement.playbookMessageEnd();
        } else {
          saveCaseId?.(caseId!);
        }
        console.log("calling messageRequestDone");
        mainIOElement.messageRequestDone();

        messageDoneCallback?.();
      },
    };

    if (mainIOElement.taskCategory === "traffic-light") {
      mainIOElement.setTrafficLightSubTopicOpenOk(false);
    }

    await startLLMStreamV2(llmStreamV2Params);
  } catch (error) {
    console.error(error);
    mainIOElement.hideAllLoadingIndicators();
    throw error;
  } finally {
    checkInputCallback(false);
  }
}

export function getWorkflowTotalTopicSteps(workflow: Workflow): Record<string, number> {
  let topicSteps: Record<string, number> = {};
  for (let workflowPrompt of workflow.prompts) {
    topicSteps[workflowPrompt.data.topic] = 1;
    // topicSteps[workflowPrompt.data.topic] += getRecursiveChildPromptDataCount(
    //   workflowPrompt.children
    // );
  }
  return topicSteps;
}

function getRecursiveChildPromptDataCount(children: WorkflowPrompt[]): number {
  let count = 0;
  for (let child of children) {
    count += 1;
    if (child.children) {
      count += getRecursiveChildPromptDataCount(child.children);
    }
  }
  return count;
}
